
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import InlineEdit from '../../../components/base/InlineEdit';

const STORAGE_KEY = 'lonetrs_reviews';

const defaultReviews = [
  {
    id: 1,
    name: 'Sarah Mitchell',
    rating: 5,
    text: 'LONE TRS made our interstate move from Las Vegas to California absolutely seamless. Their team was professional, careful with our belongings, and delivered everything on time.',
    platform: 'Google',
    avatar: 'https://public.readdy.ai/ai/img_res/26b9e1195147268419af9b3905137998.jpg',
  },
  {
    id: 2,
    name: 'Michael Rodriguez',
    rating: 5,
    text: 'Outstanding vehicle transport service! They moved my classic car from Henderson to Seattle with extreme care. The tracking system kept me updated throughout the journey.',
    platform: 'Yelp',
    avatar: 'https://readdy.ai/api/search-image?query=professional%20man%20smiling%20headshot%20portrait%20simple%20white%20background%20business%20attire%20confident%20friendly%20expression%20natural%20lighting%20high%20quality%20photography&width=100&height=100&seq=rev2&orientation=squarish',
  },
  {
    id: 3,
    name: 'Jennifer Chen',
    rating: 5,
    text: 'We used LONE TRS for our commercial relocation and they exceeded all expectations. Their crew was efficient, the pricing was transparent, and they handled our office equipment with great care.',
    platform: 'Google',
    avatar: 'https://readdy.ai/api/search-image?query=professional%20asian%20woman%20smiling%20headshot%20portrait%20simple%20white%20background%20business%20professional%20attire%20confident%20friendly%20expression%20natural%20lighting%20high%20quality%20photography&width=100&height=100&seq=rev3&orientation=squarish',
  },
  {
    id: 4,
    name: 'David Thompson',
    rating: 5,
    text: 'Best moving experience ever! LONE TRS handled our residential move from Mesquite to Portland with incredible professionalism. The team arrived on time and treated our furniture like it was their own.',
    platform: 'Google',
    avatar: 'https://readdy.ai/api/search-image?query=professional%20mature%20man%20smiling%20headshot%20portrait%20simple%20white%20background%20business%20casual%20attire%20confident%20friendly%20expression%20natural%20lighting%20high%20quality%20photography&width=100&height=100&seq=rev4&orientation=squarish',
  },
  {
    id: 5,
    name: 'Amanda Foster',
    rating: 5,
    text: 'Exceptional service for our building material transport needs. LONE TRS delivered construction materials to multiple job sites across Nevada and California without a single issue.',
    platform: 'Yelp',
    avatar: 'https://readdy.ai/api/search-image?query=professional%20woman%20blonde%20smiling%20headshot%20portrait%20simple%20white%20background%20business%20attire%20confident%20friendly%20expression%20natural%20lighting%20high%20quality%20photography&width=100&height=100&seq=rev5&orientation=squarish',
  },
];

type Review = typeof defaultReviews[0];

export default function Reviews() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [reviews, setReviews] = useState<Review[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : defaultReviews;
    } catch { return defaultReviews; }
  });

  const save = (updated: Review[]) => {
    setReviews(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  };

  const updateField = (id: number, field: keyof Review, value: string | number) => {
    save(reviews.map(r => r.id === id ? { ...r, [field]: value } : r));
  };

  const addReview = () => {
    const newReview: Review = {
      id: Date.now(),
      name: 'New Reviewer',
      rating: 5,
      text: 'Click to edit this review text...',
      platform: 'Google',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20person%20smiling%20headshot%20portrait%20simple%20white%20background%20business%20attire%20confident%20friendly%20expression%20natural%20lighting%20high%20quality%20photography&width=100&height=100&seq=revnew&orientation=squarish',
    };
    const updated = [...reviews, newReview];
    save(updated);
    setCurrentIndex(updated.length - 1);
  };

  const deleteReview = (id: number) => {
    if (reviews.length <= 1) return;
    const updated = reviews.filter(r => r.id !== id);
    save(updated);
    setCurrentIndex(Math.min(currentIndex, updated.length - 1));
  };

  const nextReview = () => setCurrentIndex(prev => (prev + 1) % reviews.length);
  const prevReview = () => setCurrentIndex(prev => (prev - 1 + reviews.length) % reviews.length);

  const getVisibleReviews = () => {
    const visible = [];
    for (let i = -1; i <= 1; i++) {
      const index = (currentIndex + i + reviews.length) % reviews.length;
      visible.push({ ...reviews[index], position: i });
    }
    return visible;
  };

  return (
    <section className="py-24 bg-gradient-to-b from-dark-900 via-dark-800 to-dark-900 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-orbitron text-5xl font-bold text-white mb-4">
            CLIENT <span className="text-electric-blue">REVIEWS</span>
          </h2>
          <p className="font-inter text-white/60 text-lg">
            Hear what our satisfied customers have to say
          </p>
          <button
            onClick={addReview}
            className="mt-4 px-5 py-2 bg-electric-blue/20 border border-electric-blue/50 text-electric-blue font-inter text-sm rounded-full hover:bg-electric-blue/30 transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line mr-1 w-4 h-4 inline-flex items-center justify-center"></i>
            Add Review
          </button>
        </motion.div>

        <div className="relative h-[440px] flex items-center justify-center">
          <AnimatePresence mode="popLayout">
            {getVisibleReviews().map((review) => (
              <motion.div
                key={`${review.id}-${review.position}`}
                initial={{ opacity: 0, scale: 0.8, x: review.position * 400 }}
                animate={{
                  opacity: review.position === 0 ? 1 : 0.3,
                  scale: review.position === 0 ? 1 : 0.8,
                  x: review.position * 400,
                  zIndex: review.position === 0 ? 10 : 0,
                }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.5 }}
                className="absolute w-full max-w-2xl"
              >
                <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-8 hover:bg-white/15 transition-all duration-300">
                  {review.position === 0 && (
                    <div className="flex justify-end mb-2">
                      <button
                        onClick={() => deleteReview(review.id)}
                        className="text-red-400/60 hover:text-red-400 text-xs font-inter flex items-center gap-1 cursor-pointer transition-colors"
                      >
                        <i className="ri-delete-bin-line w-3 h-3 flex items-center justify-center"></i>
                        Remove
                      </button>
                    </div>
                  )}

                  {/* Stars */}
                  <div className="flex justify-center mb-6 space-x-1">
                    {[1, 2, 3, 4, 5].map(star => (
                      <button
                        key={star}
                        onClick={() => review.position === 0 && updateField(review.id, 'rating', star)}
                        className={`text-2xl w-6 h-6 flex items-center justify-center transition-colors ${
                          star <= review.rating ? 'text-yellow-400' : 'text-white/20'
                        } ${review.position === 0 ? 'cursor-pointer hover:scale-110' : 'cursor-default'}`}
                      >
                        <i className="ri-star-fill"></i>
                      </button>
                    ))}
                  </div>

                  {/* Quote */}
                  <div className="relative mb-8">
                    <i className="ri-double-quotes-l text-4xl text-electric-blue/30 absolute -top-2 -left-2 w-10 h-10 flex items-center justify-center"></i>
                    <p className="font-inter italic text-white/90 text-lg leading-relaxed text-center px-8">
                      {review.position === 0 ? (
                        <InlineEdit
                          value={review.text}
                          onSave={v => updateField(review.id, 'text', v)}
                          multiline
                          rows={4}
                          className="text-white/90 italic"
                          placeholder="Enter review text..."
                        />
                      ) : review.text}
                    </p>
                  </div>

                  {/* Reviewer Info */}
                  <div className="flex items-center justify-center space-x-4">
                    <img
                      src={review.avatar}
                      alt={review.name}
                      className="w-14 h-14 rounded-full border-2 border-electric-blue/50 object-cover"
                    />
                    <div className="text-left">
                      <div className="font-inter text-white font-semibold text-base">
                        {review.position === 0 ? (
                          <InlineEdit
                            value={review.name}
                            onSave={v => updateField(review.id, 'name', v)}
                            className="text-white font-semibold"
                            placeholder="Reviewer name..."
                          />
                        ) : review.name}
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="font-inter text-white/60 text-sm">
                          Verified on{' '}
                          {review.position === 0 ? (
                            <InlineEdit
                              value={review.platform}
                              onSave={v => updateField(review.id, 'platform', v)}
                              className="text-white/60"
                              placeholder="Platform..."
                            />
                          ) : review.platform}
                        </span>
                        <i className="ri-checkbox-circle-fill text-green-400 text-sm w-4 h-4 flex items-center justify-center"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          <button
            onClick={prevReview}
            className="absolute left-0 top-1/2 transform -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center hover:bg-electric-blue/20 hover:border-electric-blue transition-all duration-300 z-20 cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-2xl text-white w-6 h-6 flex items-center justify-center"></i>
          </button>
          <button
            onClick={nextReview}
            className="absolute right-0 top-1/2 transform -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center hover:bg-electric-blue/20 hover:border-electric-blue transition-all duration-300 z-20 cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-2xl text-white w-6 h-6 flex items-center justify-center"></i>
          </button>
        </div>

        <div className="flex justify-center space-x-2 mt-12">
          {reviews.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`h-2 rounded-full transition-all duration-300 cursor-pointer ${
                index === currentIndex ? 'bg-electric-blue w-8' : 'bg-white/30 hover:bg-white/50 w-2'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
